import { IconManager as iconManager } from "../../js/helpers/iconmanager";
import { TooltipManager as tooltipManager } from "../../js/helpers/tooltipmanager";

class IconSlot extends Widget {
    _element: any;
    _type: any;
    _object: any;
    _icon: any;
    _tooltip: any;
    _tooltipExtra: any[] = [];
    _usable: any;
    _text: any;
    _textElement: any;
    _cooldownOverlay: any;
    _cooldownRafId: any;
    static MOVE_Z = 9999;

    constructor(element: any) {
        super(element);
        this._type = HOT_ICON_TYPE.EMPTY;
        this._object = null;
        this._icon = null;
        this._tooltip = null;
        this._usable = true;
        this._text = null;
        this._textElement = null;
        this._cooldownOverlay = null;
        this._cooldownRafId = null;
        this.clear();
    }

    type(type: any) {
        if (type === undefined) {
            return this._type;
        } else {
            this._type = type;
            this._update();
        }
    }

    icon(object: any) {
        if (object === undefined) {
            return this._object;
        } else {
            this._object = object;
            this._update();
        }
    }

    text(text: any) {
        if (text === undefined) {
            return this._text;
        } else {
            this._text = text;
            this._update();
        }
    }

    clear() {
        this._type = HOT_ICON_TYPE.EMPTY;
        this._object = null;
        // Don't clear _text, as it's user-set
        this._update();
    }

    setIcon(type: any, icon: any) {
        this._type = type;
        this._object = icon;
        this._update();
    }

    setItem(item: any) {
        this._type = HOT_ICON_TYPE.ITEM;
        this._object = item;
        this._update();
    }

    // Appends extra HTML to the tooltip _update() just built (e.g. an NPC
    // store's price line) without teaching tooltipManager about callers
    // that aren't real inventory items.
    appendTooltip(html: any) {
        this._tooltipExtra.push(html);
        if (this._tooltip) {
            this._tooltip.append(html);
        }
    }

    setCommand(command: any) {
        this._type = HOT_ICON_TYPE.COMMAND;
        this._object = command;
        this._update();
    }

    setSkill(skill: any) {
        this._type = HOT_ICON_TYPE.SKILL;
        this._object = skill;
        this._update();
    }

    setEmote(emote: any) {
        this._type = HOT_ICON_TYPE.EMOTE;
        this._object = emote;
        this._update();
    }

    setDialog(dialog: any) {
        this._type = HOT_ICON_TYPE.DIALOG;
        this._object = dialog;
        this._update();
    }

    setClanSkill(skill: any) {
        this._type = HOT_ICON_TYPE.CLAN_SKILL;
        this._object = skill;
        this._update();
    }

    dragEnabled(drag?: any) {
        if (drag === undefined) {
            return !this._element.hasClass("nodrag");
        } else if (drag !== this.dragEnabled) {
            this._element.toggleClass("nodrag");
        }
    }

    acceptsAll(accept?: any) {
        if (accept === undefined) {
            return this._element.hasClass("accepts-all");
        } else if (accept !== this.acceptsAll()) {
            this._element.toggleClass("accepts-all");
        }
    }

    acceptsSkill(accept?: any) {
        if (accept === undefined) {
            return this._element.hasClass("accepts-skill");
        } else if (accept !== this.acceptsSkill()) {
            this._element.toggleClass("accepts-skill");
        }
    }

    acceptsItem(accept?: any) {
        if (accept === undefined) {
            return this._element.hasClass("accepts-item");
        } else if (accept !== this.acceptsItem()) {
            this._element.toggleClass("accepts-item");
        }
    }

    use() {
        switch (this._type) {
            case HOT_ICON_TYPE.ITEM:
                MC.inventory.useItem(this._object);
                break;
            case HOT_ICON_TYPE.SKILL:
                MC.skills.useSkill(this._object);
                break;
            case HOT_ICON_TYPE.COMMAND:
            case HOT_ICON_TYPE.EMOTE:
            case HOT_ICON_TYPE.DIALOG:
            case HOT_ICON_TYPE.CLAN_SKILL:
            default:
                console.warn(
                    "Unimplemented hot icon type in IconSlot.use",
                    this._type
                );
        }
    }

    _onSwap(other: any) {
        this.emit("swap", other);
    }

    _onDoubleClick() {
        tooltipManager.hideTooltip();
        if (this._usable) {
            this.use();
        }
    }

    // Item tooltips depend on live player stats (requirement met/unmet),
    // so rebuild on hover instead of trusting the one built in _update().
    _onMouseEnter() {
        if (this._type !== HOT_ICON_TYPE.ITEM) return;
        this._tooltip = tooltipManager.getItemTooltip(this._object);
        this._tooltipExtra.forEach((html) => this._tooltip.append(html));
    }

    _onMouseMove(moveEvent: any) {
        tooltipManager.showTooltip(
            this._tooltip,
            moveEvent.clientX,
            moveEvent.clientY
        );
    }

    _onMouseOut(outEvent: any) {
        tooltipManager.hideTooltip();
    }

    // iOS doesn't reliably fire dblclick for touch, so a second quick tap counts as one.
    // Mouse and any dblclick the browser does fire are de-duplicated by the 500ms guard.
    _lastTapAt = 0;
    _lastDoubleAt = 0;
    _dragged = false;

    _onPointerUp(upEvent: any) {
        if (upEvent.originalEvent.pointerType !== "touch") return;
        if (this._dragged) {
            this._dragged = false;
            return;
        }
        const now = Date.now();
        if (now - this._lastTapAt < 350) {
            this._lastTapAt = 0;
            this._lastDoubleAt = now;
            this._onDoubleClick();
        } else {
            this._lastTapAt = now;
            this._tapTooltipPending = true;
        }
    }

    // Touch has no hover: a single tap shows the tooltip instead. Shown on the click that follows
    // the tap, because the emulated mousedown before it hides every tooltip.
    _tapTooltipPending = false;

    _onClick(clickEvent: any) {
        if (!this._tapTooltipPending) return;
        this._tapTooltipPending = false;
        this._onMouseEnter();
        if (this._tooltip) tooltipManager.showTooltip(this._tooltip, clickEvent.clientX, clickEvent.clientY);
    }

    _onMouseDown(downEvent: any) {
        tooltipManager.hideTooltip();
        if (downEvent.which !== 1) {
            return;
        }

        if (!this.dragEnabled()) {
            return;
        }

        const self = this;
        const offset = self._icon.offset();
        let dragStarted = false;

        function mouseMove(moveEvent: any) {
            if (!dragStarted) {
                dragStarted = true;
                self._dragged = true;
                tooltipManager.hideTooltip();

                self._icon.css("position", "absolute");
                self._icon.css("z-index", IconSlot.MOVE_Z);
                self._icon.detach();
                $(".ui").append(self._icon);
                self._icon.offset({
                    left: offset.left,
                    top: offset.top,
                });
            }

            self._icon.offset({
                left: moveEvent.pageX - downEvent.pageX + offset.left,
                top: moveEvent.pageY - downEvent.pageY + offset.top,
            });
        }

        function mouseUp(upEvent: any) {
            $(document).off("pointermove", mouseMove);
            $(document).off("pointerup pointercancel", mouseUp);

            if (!dragStarted) {
                return;
            }

            self._icon.hide();
            let target = $(
                document.elementFromPoint(upEvent.clientX, upEvent.clientY)
            );
            self._icon.css("z-index", "");
            self._icon.css("position", "relative");
            self._icon.show();
            self._icon.detach();
            self._element.append(self._icon);
            self._icon.offset(offset);

            if (!target.hasClass("slot")) {
                const parent = target.parent();

                if (parent.hasClass("slot")) {
                    target = parent;
                }
            }

            // A quickbar icon dropped anywhere outside a quickbar slot is removed.
            // Other icon sources keep their existing canvas and slot behavior.
            const fromQuickbar = /(?:^|\s)quick-slot-\d+(?:\s|$)/.test(self._element.attr("class") || "");
            const toQuickbar = target.hasClass("slot") && /(?:^|\s)quick-slot-\d+(?:\s|$)/.test(target.attr("class") || "");

            if (fromQuickbar && !toQuickbar) {
                self._onSwap("drop");
            } else if (target.is("canvas")) {
                self._onSwap("drop");
            } else if (target.hasClass("slot") && !target.is(self._element)) {
                const otherClass = target.attr("class");
                const match = otherClass.match(/[a-z\-]*-slot-[0-9]*/);

                if (match) {
                    self._onSwap(match[0]);
                }
            }
        }

        $(document).on("pointermove", mouseMove);
        $(document).on("pointerup pointercancel", mouseUp);
    }

    _update() {
        tooltipManager.hideTooltip();
        if (this._icon) {
            this._icon.remove();
        }
        if (this._textElement) {
            this._textElement.remove();
        }
        if (this._cooldownOverlay) {
            this._cooldownOverlay.remove();
        }
        if (this._cooldownRafId !== null) {
            cancelAnimationFrame(this._cooldownRafId);
        }

        this._icon = null;
        this._tooltip = null;
        this._tooltipExtra = [];
        this._textElement = null;
        this._cooldownOverlay = null;
        this._cooldownRafId = null;
        this._element.removeClass("cooling-down");

        switch (this._type) {
            case HOT_ICON_TYPE.ITEM:
                this._icon = iconManager.getItemIcon(this._object);
                this._tooltip = tooltipManager.getItemTooltip(this._object);
                break;
            case HOT_ICON_TYPE.COMMAND:
                this._icon = iconManager.getCommandIcon(this._object);
                this._tooltip = tooltipManager.getCommandTooltip(this._object);
                break;
            case HOT_ICON_TYPE.SKILL:
                this._icon = iconManager.getSkillIcon(this._object);
                this._tooltip = tooltipManager.getSkillTooltip(this._object);
                break;
            case HOT_ICON_TYPE.EMOTE:
                this._icon = iconManager.getEmoteIcon(this._object);
                this._tooltip = tooltipManager.getEmoteTooltip(this._object);
                break;
            case HOT_ICON_TYPE.DIALOG:
                this._icon = iconManager.getDialogIcon(this._object);
                this._tooltip = tooltipManager.getDialogTooltip(this._object);
                break;
            case HOT_ICON_TYPE.CLAN_SKILL:
                this._icon = iconManager.getClanSkillIcon(this._object);
                this._tooltip = tooltipManager.getClanSkillTooltip(this._object);
                break;
        }

        if (this._icon) {
            this._icon.mouseenter(this._onMouseEnter.bind(this));
            this._icon.mousemove(this._onMouseMove.bind(this));
            this._icon.mouseout(this._onMouseOut.bind(this));
            this._icon.on("pointerdown", this._onMouseDown.bind(this));
            this._icon.dblclick(() => {
                // skip if the touch double-tap path already handled this gesture
                if (Date.now() - this._lastDoubleAt > 500) this._onDoubleClick();
            });
            this._icon.css("touch-action", "none");
            this._icon.on("pointerup", this._onPointerUp.bind(this));
            this._icon.on("click", this._onClick.bind(this));
            this._element.append(this._icon);
        }

        let countdown: { startedAt: number; durationMs: number } | null = null;
        if (this._type === HOT_ICON_TYPE.SKILL && this._object) {
            countdown = MC.skills.getCooldown(this._object.skillIdx);
        } else if (this._type === HOT_ICON_TYPE.ITEM && this._object) {
            countdown = MC.inventory.getCooldown(this._object);
        }

        if (countdown) {
            this._element.addClass("cooling-down");
            this._cooldownOverlay = $('<div class="cooldown-overlay"></div>');
            this._element.append(this._cooldownOverlay);

            const tick = () => {
                const pct = Math.min(
                    100,
                    ((Date.now() - countdown!.startedAt) / countdown!.durationMs) * 100
                );
                this._cooldownOverlay.css("--cd-pct", pct + "%");

                if (pct >= 100) {
                    this._cooldownOverlay.remove();
                    this._cooldownOverlay = null;
                    this._cooldownRafId = null;
                    this._element.removeClass("cooling-down");
                } else {
                    this._cooldownRafId = requestAnimationFrame(tick);
                }
            };
            tick();
        }

        if (this._text && !this._icon) {
            this._textElement = $('<div class="slot-text"></div>');
            this._textElement.text(this._text);
            this._textElement.css({
                "font-size": "10px",
                "text-align": "center",
                position: "absolute",
                top: "50%",
                left: "0",
                right: "0",
                transform: "translateY(-50%)",
            });
            this._element.append(this._textElement);
        }
    }

    static Create() {
        return $('<div class="slot" />');
    }
}

ui.IconSlot = IconSlot;
ui.iconslot = ui.widgetConstructor("slot", ui.IconSlot);
